import React, { useState, useEffect, useCallback } from 'react';
import { Shield, AlertTriangle, DollarSign, Target, FileText, Send, Calculator, ChevronDown, ChevronUp, Activity, Zap } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  detected: { label: 'DETECTED', color: '#00F0FF', bg: 'rgba(0,240,255,0.1)' },
  notice_sent: { label: 'NOTICE SENT', color: '#F59E0B', bg: 'rgba(245,158,11,0.1)' },
  settlement_pending: { label: 'PENDING', color: '#F97316', bg: 'rgba(249,115,22,0.1)' },
  settled: { label: 'SETTLED', color: '#22C55E', bg: 'rgba(34,197,94,0.1)' },
  dmca_filed: { label: 'DMCA FILED', color: '#FF00E5', bg: 'rgba(255,0,229,0.1)' },
  resolved: { label: 'RESOLVED', color: '#6B7280', bg: 'rgba(107,114,128,0.1)' },
};

const PLATFORM_ICONS = {
  YouTube: 'YT', TikTok: 'TT', Instagram: 'IG', Facebook: 'FB',
  Twitter: 'TW', Spotify: 'SP', Pinterest: 'PN',
};

function AnimatedNumber({ value, prefix = '', suffix = '', decimals = 0 }) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    let start = 0;
    const end = value;
    const dur = 1200;
    const step = end / (dur / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= end) { setDisplay(end); clearInterval(timer); }
      else setDisplay(start);
    }, 16);
    return () => clearInterval(timer);
  }, [value]);
  const formatted = decimals > 0 ? display.toFixed(decimals) : Math.floor(display).toLocaleString();
  return <span>{prefix}{formatted}{suffix}</span>;
}

function StatCard({ icon: Icon, label, value, prefix, suffix, color, decimals = 0 }) {
  return (
    <div
      className="rounded-xl p-4 transition-all hover:scale-[1.02]"
      style={{
        background: 'rgba(20,20,20,0.8)',
        border: `1px solid ${color}22`,
        boxShadow: `0 0 20px ${color}08`,
      }}
      data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}
    >
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} style={{ color }} />
        <span className="font-label text-[10px]" style={{ color: '#6B7280' }}>{label}</span>
      </div>
      <div className="text-2xl font-bold" style={{ fontFamily: "'Orbitron', sans-serif", color }}>
        <AnimatedNumber value={value} prefix={prefix} suffix={suffix} decimals={decimals} />
      </div>
    </div>
  );
}

function PipelineStage({ label, count, color, isLast }) {
  return (
    <div className="flex items-center flex-1 min-w-[80px]">
      <div className="flex flex-col items-center flex-1">
        <div
          className="w-full rounded-lg px-2 py-3 text-center transition-all"
          style={{
            background: count > 0 ? `${color}15` : 'rgba(20,20,20,0.5)',
            border: `1px solid ${count > 0 ? color + '40' : '#2A2A2A'}`,
            boxShadow: count > 0 ? `0 0 12px ${color}15` : 'none',
          }}
        >
          <div className="text-xl font-bold" style={{ fontFamily: "'Orbitron', sans-serif", color: count > 0 ? color : '#4B5563' }}>
            {count}
          </div>
          <div className="text-[9px] font-label mt-1" style={{ color: count > 0 ? color : '#4B5563' }}>
            {label}
          </div>
        </div>
      </div>
      {!isLast && (
        <div className="w-6 flex items-center justify-center shrink-0">
          <div className="w-4 h-px" style={{ background: '#2A2A2A' }} />
          <div className="text-[8px]" style={{ color: '#4B5563' }}>&#9654;</div>
        </div>
      )}
    </div>
  );
}

function CaseCard({ c, onSendNotice, onGenerateInvoice }) {
  const [expanded, setExpanded] = useState(false);
  const st = STATUS_CONFIG[c.status] || STATUS_CONFIG.detected;

  return (
    <div
      className="rounded-xl overflow-hidden transition-all"
      style={{
        background: 'rgba(20,20,20,0.6)',
        border: `1px solid ${st.color}20`,
      }}
      data-testid={`case-${c.case_number}`}
    >
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-[rgba(255,255,255,0.02)] transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        {/* Platform badge */}
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center text-[10px] font-bold shrink-0"
          style={{ background: st.bg, color: st.color, border: `1px solid ${st.color}30` }}
        >
          {PLATFORM_ICONS[c.platform] || '??'}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-label" style={{ color: '#6B7280' }}>{c.case_number}</span>
            <span className="text-xs font-semibold truncate" style={{ color: '#E5E7EB' }}>{c.asset_title}</span>
          </div>
          <div className="text-[11px] mt-0.5" style={{ color: '#9CA3AF' }}>
            {c.infringer} · {c.platform} · {c.asset_type}
          </div>
        </div>

        {/* Confidence */}
        <div className="hidden sm:flex flex-col items-end shrink-0">
          <span className="text-[10px] font-label" style={{ color: '#6B7280' }}>MATCH</span>
          <div className="flex items-center gap-1.5">
            <div className="w-16 h-1.5 rounded-full bg-[#1A1A1A] overflow-hidden">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${c.match_confidence}%`,
                  background: c.match_confidence > 95 ? '#22C55E' : c.match_confidence > 90 ? '#F59E0B' : '#EF4444',
                }}
              />
            </div>
            <span className="text-xs font-bold" style={{ color: '#E5E7EB' }}>{c.match_confidence}%</span>
          </div>
        </div>

        {/* Amount */}
        <div className="hidden sm:flex flex-col items-end shrink-0 mr-2">
          <span className="text-[10px] font-label" style={{ color: '#6B7280' }}>SETTLEMENT</span>
          <span className="text-sm font-bold" style={{ fontFamily: "'Orbitron', sans-serif", color: '#00F0FF' }}>
            ${c.settlement_amount?.toLocaleString()}
          </span>
        </div>

        {/* Status badge */}
        <span
          className="px-2 py-1 rounded-md text-[10px] font-bold shrink-0"
          style={{ background: st.bg, color: st.color, border: `1px solid ${st.color}30` }}
        >
          {st.label}
        </span>

        {expanded ? <ChevronUp size={14} style={{ color: '#6B7280' }} /> : <ChevronDown size={14} style={{ color: '#6B7280' }} />}
      </div>

      {expanded && (
        <div className="px-4 pb-4 pt-2 border-t" style={{ borderColor: `${st.color}10` }}>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
            <div>
              <p className="text-[9px] font-label" style={{ color: '#6B7280' }}>AD SPEND DETECTED</p>
              <p className="text-sm font-bold" style={{ color: '#E5E7EB' }}>${c.ad_spend_detected?.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-[9px] font-label" style={{ color: '#6B7280' }}>SETTLEMENT</p>
              <p className="text-sm font-bold" style={{ color: '#00F0FF' }}>${c.settlement_amount?.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-[9px] font-label" style={{ color: '#6B7280' }}>BOUNTY ({c.bounty_percentage}%)</p>
              <p className="text-sm font-bold" style={{ color: '#F59E0B' }}>${c.bounty_amount?.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-[9px] font-label" style={{ color: '#6B7280' }}>CREATOR PAYOUT</p>
              <p className="text-sm font-bold" style={{ color: '#22C55E' }}>${c.creator_payout?.toLocaleString()}</p>
            </div>
          </div>

          <div className="text-[11px] mb-3" style={{ color: '#9CA3AF' }}>
            <span className="font-label text-[9px]" style={{ color: '#6B7280' }}>CAMPAIGN: </span>
            <a href={c.campaign_url} target="_blank" rel="noreferrer" className="underline hover:text-white transition-colors">{c.campaign_url}</a>
          </div>

          <div className="flex gap-2 flex-wrap">
            {c.status === 'detected' && (
              <button
                onClick={(e) => { e.stopPropagation(); onSendNotice(c.id); }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all hover:scale-105"
                style={{ background: 'rgba(245,158,11,0.15)', color: '#F59E0B', border: '1px solid rgba(245,158,11,0.3)' }}
                data-testid={`send-notice-${c.case_number}`}
              >
                <Send size={12} /> SEND NOTICE
              </button>
            )}
            {['notice_sent', 'settlement_pending', 'settled'].includes(c.status) && (
              <button
                onClick={(e) => { e.stopPropagation(); onGenerateInvoice(c.id); }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all hover:scale-105"
                style={{ background: 'rgba(0,240,255,0.1)', color: '#00F0FF', border: '1px solid rgba(0,240,255,0.2)' }}
                data-testid={`gen-invoice-${c.case_number}`}
              >
                <FileText size={12} /> GENERATE INVOICE
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function EnforcementDashboard() {
  const [stats, setStats] = useState(null);
  const [pipeline, setPipeline] = useState({});
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [calcInputs, setCalcInputs] = useState({ ad_spend: 100000, platform: 'YouTube', content_type: 'audio', usage_duration_days: 30 });
  const [calcResult, setCalcResult] = useState(null);
  const [invoices, setInvoices] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, pipeRes, casesRes, invRes] = await Promise.all([
        fetch(`${API}/enforcement/stats`),
        fetch(`${API}/enforcement/pipeline`),
        fetch(`${API}/enforcement/cases`),
        fetch(`${API}/enforcement/invoices`),
      ]);
      const [statsData, pipeData, casesData, invData] = await Promise.all([
        statsRes.json(), pipeRes.json(), casesRes.json(), invRes.json(),
      ]);
      setStats(statsData);
      setPipeline(pipeData);
      setCases(casesData.cases || []);
      setInvoices(invData.invoices || []);
    } catch (e) {
      console.error('Failed to fetch enforcement data:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Seed enforcement data first, then fetch
    fetch(`${API}/enforcement/seed`, { method: 'POST' })
      .catch(() => {})
      .finally(() => fetchData());
  }, [fetchData]);

  const handleSendNotice = async (caseId) => {
    try {
      await fetch(`${API}/enforcement/cases/${caseId}/send-notice`, { method: 'POST' });
      fetchData();
    } catch (e) { console.error(e); }
  };

  const handleGenerateInvoice = async (caseId) => {
    try {
      await fetch(`${API}/enforcement/cases/${caseId}/generate-invoice`, { method: 'POST' });
      fetchData();
    } catch (e) { console.error(e); }
  };

  const handleCalculate = async () => {
    try {
      const res = await fetch(`${API}/enforcement/calculate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(calcInputs),
      });
      setCalcResult(await res.json());
    } catch (e) { console.error(e); }
  };

  useEffect(() => { handleCalculate(); }, []);

  const filteredCases = filterStatus === 'all' ? cases : cases.filter(c => c.status === filterStatus);

  const pipelineStages = [
    { key: 'detected', label: 'DETECTED', color: '#00F0FF' },
    { key: 'notice_sent', label: 'NOTICED', color: '#F59E0B' },
    { key: 'settlement_pending', label: 'PENDING', color: '#F97316' },
    { key: 'settled', label: 'SETTLED', color: '#22C55E' },
    { key: 'dmca_filed', label: 'DMCA', color: '#FF00E5' },
    { key: 'resolved', label: 'RESOLVED', color: '#6B7280' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0A0A0C' }}>
        <div className="text-center">
          <Activity size={32} className="animate-spin mx-auto mb-3" style={{ color: '#00F0FF' }} />
          <p className="font-label text-[11px]" style={{ color: '#6B7280' }}>INITIALIZING ENFORCEMENT SYSTEMS...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-20" style={{ background: '#0A0A0C' }} data-testid="enforcement-dashboard">
      {/* Header */}
      <div className="border-b" style={{ borderColor: '#1A1A1A', background: 'linear-gradient(180deg, rgba(0,59,111,0.08) 0%, transparent 100%)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
          <div className="flex items-center gap-3 mb-1">
            <Shield size={20} style={{ color: '#FF00E5' }} />
            <h1 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: "'Orbitron', sans-serif", color: '#FF00E5', letterSpacing: '0.1em' }}>
              VELVET HAMMER
            </h1>
            <div className="flex items-center gap-1.5 ml-3 px-2 py-0.5 rounded-full" style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)' }}>
              <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: '#22C55E' }} />
              <span className="text-[10px] font-label" style={{ color: '#22C55E' }}>SYSTEMS ONLINE</span>
            </div>
          </div>
          <p className="text-sm" style={{ color: '#6B7280', fontFamily: "'Jost', sans-serif" }}>
            IP Enforcement Command · Automated DMCA &amp; Settlement Engine
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Stats Grid */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="stats-grid">
            <StatCard icon={Target} label="TOTAL CASES" value={stats.total_cases} color="#00F0FF" />
            <StatCard icon={DollarSign} label="RECOVERED" value={stats.total_recovered} prefix="$" color="#22C55E" />
            <StatCard icon={AlertTriangle} label="ACTIVE NOTICES" value={stats.active_notices} color="#F59E0B" />
            <StatCard icon={Zap} label="AVG CONFIDENCE" value={stats.avg_confidence} suffix="%" color="#8B5CF6" decimals={1} />
            <StatCard icon={Shield} label="SUCCESS RATE" value={stats.success_rate} suffix="%" color="#FF00E5" decimals={1} />
            <StatCard icon={FileText} label="INVOICES" value={stats.invoices_generated} color="#00F0FF" />
          </div>
        )}

        {/* Pipeline */}
        <div className="rounded-xl p-4" style={{ background: 'rgba(20,20,20,0.5)', border: '1px solid #1A1A1A' }} data-testid="enforcement-pipeline">
          <h2 className="text-xs font-label mb-4" style={{ color: '#6B7280' }}>ENFORCEMENT PIPELINE</h2>
          <div className="flex gap-1 overflow-x-auto pb-1">
            {pipelineStages.map((s, i) => (
              <PipelineStage key={s.key} label={s.label} count={pipeline[s.key] || 0} color={s.color} isLast={i === pipelineStages.length - 1} />
            ))}
          </div>
        </div>

        {/* Cases Section */}
        <div data-testid="cases-section">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h2 className="text-xs font-label" style={{ color: '#6B7280' }}>ENFORCEMENT CASES ({filteredCases.length})</h2>
            <div className="flex gap-1 flex-wrap">
              <button
                onClick={() => setFilterStatus('all')}
                className="px-2 py-1 rounded text-[10px] font-bold transition-all"
                style={{
                  background: filterStatus === 'all' ? 'rgba(0,240,255,0.15)' : 'transparent',
                  color: filterStatus === 'all' ? '#00F0FF' : '#6B7280',
                  border: `1px solid ${filterStatus === 'all' ? 'rgba(0,240,255,0.3)' : '#2A2A2A'}`,
                }}
                data-testid="filter-all"
              >
                ALL
              </button>
              {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                <button
                  key={key}
                  onClick={() => setFilterStatus(key)}
                  className="px-2 py-1 rounded text-[10px] font-bold transition-all"
                  style={{
                    background: filterStatus === key ? `${cfg.color}15` : 'transparent',
                    color: filterStatus === key ? cfg.color : '#6B7280',
                    border: `1px solid ${filterStatus === key ? cfg.color + '30' : '#2A2A2A'}`,
                  }}
                  data-testid={`filter-${key}`}
                >
                  {cfg.label}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            {filteredCases.map(c => (
              <CaseCard key={c.id} c={c} onSendNotice={handleSendNotice} onGenerateInvoice={handleGenerateInvoice} />
            ))}
            {filteredCases.length === 0 && (
              <div className="text-center py-12" style={{ color: '#4B5563' }}>
                <Target size={32} className="mx-auto mb-2 opacity-30" />
                <p className="text-sm">No cases matching filter</p>
              </div>
            )}
          </div>
        </div>

        {/* Bottom row: Calculator + Invoices */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Settlement Calculator */}
          <div className="rounded-xl p-5" style={{ background: 'rgba(20,20,20,0.5)', border: '1px solid #1A1A1A' }} data-testid="settlement-calculator">
            <div className="flex items-center gap-2 mb-4">
              <Calculator size={16} style={{ color: '#00F0FF' }} />
              <h2 className="text-xs font-label" style={{ color: '#6B7280' }}>SETTLEMENT CALCULATOR</h2>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="text-[9px] font-label block mb-1" style={{ color: '#6B7280' }}>AD SPEND ($)</label>
                <input
                  type="number"
                  value={calcInputs.ad_spend}
                  onChange={e => setCalcInputs(p => ({ ...p, ad_spend: Number(e.target.value) }))}
                  className="w-full rounded-lg px-3 py-2 text-sm text-white outline-none"
                  style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid #2A2A2A', fontFamily: "'Jost', sans-serif" }}
                  data-testid="calc-ad-spend"
                />
              </div>
              <div>
                <label className="text-[9px] font-label block mb-1" style={{ color: '#6B7280' }}>PLATFORM</label>
                <select
                  value={calcInputs.platform}
                  onChange={e => setCalcInputs(p => ({ ...p, platform: e.target.value }))}
                  className="w-full rounded-lg px-3 py-2 text-sm text-white outline-none"
                  style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid #2A2A2A', fontFamily: "'Jost', sans-serif" }}
                  data-testid="calc-platform"
                >
                  {['YouTube', 'TikTok', 'Instagram', 'Facebook', 'Twitter', 'Spotify', 'Pinterest'].map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[9px] font-label block mb-1" style={{ color: '#6B7280' }}>CONTENT TYPE</label>
                <select
                  value={calcInputs.content_type}
                  onChange={e => setCalcInputs(p => ({ ...p, content_type: e.target.value }))}
                  className="w-full rounded-lg px-3 py-2 text-sm text-white outline-none"
                  style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid #2A2A2A', fontFamily: "'Jost', sans-serif" }}
                  data-testid="calc-content-type"
                >
                  {['audio', 'video', 'image', 'text'].map(t => (
                    <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[9px] font-label block mb-1" style={{ color: '#6B7280' }}>USAGE (DAYS)</label>
                <input
                  type="number"
                  value={calcInputs.usage_duration_days}
                  onChange={e => setCalcInputs(p => ({ ...p, usage_duration_days: Number(e.target.value) }))}
                  className="w-full rounded-lg px-3 py-2 text-sm text-white outline-none"
                  style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid #2A2A2A', fontFamily: "'Jost', sans-serif" }}
                  data-testid="calc-usage-days"
                />
              </div>
            </div>

            <button
              onClick={handleCalculate}
              className="w-full py-2 rounded-lg text-xs font-bold transition-all hover:scale-[1.01]"
              style={{ background: 'linear-gradient(135deg, #00F0FF, #003B6F)', color: '#000' }}
              data-testid="calc-submit"
            >
              CALCULATE SETTLEMENT
            </button>

            {calcResult && (
              <div className="mt-4 rounded-lg p-3" style={{ background: 'rgba(0,240,255,0.05)', border: '1px solid rgba(0,240,255,0.1)' }}>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div>
                    <p className="text-[9px] font-label" style={{ color: '#6B7280' }}>SETTLEMENT</p>
                    <p className="text-lg font-bold" style={{ fontFamily: "'Orbitron', sans-serif", color: '#00F0FF' }}>
                      ${calcResult.estimated_settlement?.toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-[9px] font-label" style={{ color: '#6B7280' }}>BOUNTY (25%)</p>
                    <p className="text-lg font-bold" style={{ fontFamily: "'Orbitron', sans-serif", color: '#F59E0B' }}>
                      ${calcResult.bounty_amount?.toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-[9px] font-label" style={{ color: '#6B7280' }}>YOUR PAYOUT</p>
                    <p className="text-lg font-bold" style={{ fontFamily: "'Orbitron', sans-serif", color: '#22C55E' }}>
                      ${calcResult.creator_payout?.toLocaleString()}
                    </p>
                  </div>
                </div>
                <p className="text-[10px] mt-3 text-center" style={{ color: '#6B7280' }}>
                  {calcResult.legal_basis}
                </p>
              </div>
            )}
          </div>

          {/* Recent Invoices */}
          <div className="rounded-xl p-5" style={{ background: 'rgba(20,20,20,0.5)', border: '1px solid #1A1A1A' }} data-testid="invoices-section">
            <div className="flex items-center gap-2 mb-4">
              <FileText size={16} style={{ color: '#00F0FF' }} />
              <h2 className="text-xs font-label" style={{ color: '#6B7280' }}>GENERATED INVOICES</h2>
            </div>

            {invoices.length > 0 ? (
              <div className="space-y-2 max-h-[340px] overflow-y-auto">
                {invoices.map(inv => (
                  <div
                    key={inv.id}
                    className="rounded-lg px-3 py-2.5 flex items-center justify-between"
                    style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid #1A1A1A' }}
                  >
                    <div>
                      <p className="text-xs font-bold" style={{ color: '#E5E7EB' }}>{inv.invoice_number}</p>
                      <p className="text-[10px]" style={{ color: '#6B7280' }}>{inv.infringer} · {inv.platform}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold" style={{ fontFamily: "'Orbitron', sans-serif", color: '#00F0FF' }}>
                        ${inv.settlement_amount?.toLocaleString()}
                      </p>
                      <span className="text-[9px] font-label px-1.5 py-0.5 rounded" style={{ background: 'rgba(34,197,94,0.1)', color: '#22C55E' }}>
                        {inv.status?.toUpperCase()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8" style={{ color: '#4B5563' }}>
                <FileText size={28} className="mx-auto mb-2 opacity-30" />
                <p className="text-sm">No invoices generated yet</p>
                <p className="text-[11px] mt-1">Click "Generate Invoice" on a case to create one</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
